import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer, Select, Spin, Input, Button } from "antd";
import Swal from "sweetalert2";
import {
  UPDATEFILTERSTATUS,
  getApiBaseUrl,
  GETMASTERSREASONS,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";

const { Option } = Select;
const { TextArea } = Input;

const StatusUpdate = ({ selectedAsset, visible, onClose }) => {
  const [status, setStatus] = useState("");
  const [formData, setFormData] = useState({
    Remarks: "",
    washingEquipId: null,
    washingReasonId: null,
    RoWaterPressure: "",
    DryerEquipId: null,
    Duration: null,
    AirPressure: "",
    Temperature: "",
  });
  const [newEquipmentsData, setNewEquipmentsData] = useState([]);
  const [washingReasons, setWashingReasons] = useState([]);
  const [airPressureRange, setAirPressureRange] = useState({
    min: 0,
    max: 0,
    unit: "bar",
    leastCount: 0.1,
    operatingMin: 0,
    operatingMax: 0,
  });
  const [roPressureRange, setRoPressureRange] = useState({
    min: 0,
    max: 0,
    unit: "bar",
    leastCount: 0.1,
    operatingMin: 0,
    operatingMax: 0,
  });
  const [temperatureRange, setTemperatureRange] = useState({
    min: 0,
    max: 0,
    unit: "°C",
    leastCount: 0.1,
    operatingMin: 0,
    operatingMax: 0,
  });
  const [pressureOptions, setPressureOptions] = useState([]);
  const [airPressureSelectOptions, setAirPressureSelectOptions] = useState([]);
  const [temperatureOptions, setTemperatureOptions] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isDeviation, setIsDeviation] = useState(false);
  const [isReauthenticated, setIsReauthenticated] = useState(false);
  const [deviationMessage, setDeviationMessage] = useState("");
  const selectedFilter = selectedAsset;
  const selectedBlock = selectedFilter?.Location;
  const selectedBarcode = selectedFilter?.Barcode;

  const processOrderMapping = {
    "Wash In": 1,
    "Wash Out": 2,
    "Dry In": 3,
    "Dry Out": 4,
    "Storage-In": 5,
    "Cleaning Cycle Completed": 6,
    "Dry In Queue": 31,
  };

  const stageNameMapping = {
    0: "To be Cleaned",
    1: "Wash In",
    2: "Wash Out",
    3: "Dry In",
    4: "Dry Out",
    5: "Storage-In",
    6: "Cleaning Cycle Completed",
    31: "Dry In Queue",
  };

  const auditLogDescription = {
    "Wash In": "Washing In Process",
    "Wash Out": "Washing Out Process",
    "Dry In": "Drying In Process",
    "Dry Out": "Drying Out Process",
    "Storage-In": "Storage In Process",
    "Cleaning Cycle Completed": "Cleaning Cycle Completed",
  };

  const fetchData = async () => {
    if (!selectedBlock) return;
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/getPressureByBlock?blockId=${selectedBlock}`
      );
      if (!res.ok) throw new Error("Failed to fetch pressure data");
      const data = await res.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        const equipments = data.ResultData.filter(
          (item) => item.Type === 0
        ).map((item) => ({
          Id: item.EquipmentId,
          EquipmentName: item.Name,
          SerialNumber: item.EquipmentId,
          BlockId: item.BlockId,
        }));

        const air = data.ResultData.find((item) => item.Type === 1);
        const water = data.ResultData.find((item) => item.Type === 2);
        const temp = data.ResultData.find((item) => item.Type === 3);

        setNewEquipmentsData(equipments);

        if (air && air.Min !== null && air.max !== null) {
          setAirPressureRange({
            min: air.Min,
            max: air.max,
            unit: air.Unit,
            leastCount: air.LeastCount ?? 0.1,
            operatingMin: air.OperatingMin ?? air.Min,
            operatingMax: air.OperatingMax ?? air.max,
          });
        } else {
          console.warn("Invalid air pressure data:", air);
          setAirPressureRange({
            min: 0,
            max: 0,
            unit: "bar",
            leastCount: 0.1,
            operatingMin: 0,
            operatingMax: 0,
          });
        }

        if (water && water.Min !== null && water.max !== null) {
          setRoPressureRange({
            min: water.Min,
            max: water.max,
            unit: water.Unit,
            leastCount: water.LeastCount ?? 0.1,
            operatingMin: water.OperatingMin ?? water.Min,
            operatingMax: water.OperatingMax ?? water.max,
          });
        } else {
          console.warn("Invalid water pressure data:", water);
          setRoPressureRange({
            min: 0,
            max: 0,
            unit: "bar",
            leastCount: 0.1,
            operatingMin: 0,
            operatingMax: 0,
          });
        }

        if (temp && temp.Min !== null && temp.max !== null) {
          setTemperatureRange({
            min: temp.Min,
            max: temp.max,
            unit: temp.Unit,
            leastCount: temp.LeastCount ?? 0.1,
            operatingMin:
              temp.OperatingMin === "NA" ? temp.Min : temp.OperatingMin,
            operatingMax:
              temp.OperatingMax === "NA" ? temp.max : temp.OperatingMax,
          });
        } else {
          console.warn("Invalid temperature data:", temp);
          setTemperatureRange({
            min: 0,
            max: 0,
            unit: "°C",
            leastCount: 0.1,
            operatingMin: 0,
            operatingMax: 0,
          });
        }
      } else {
        console.warn("Invalid response data:", data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBlock]);

  const fetchPressureOptions = async () => {
    if (!selectedBlock) return;
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/getPressureWithStepInc?blockId=${selectedBlock}`
      );
      if (!response.ok) throw new Error("Failed to fetch pressure options");
      const data = await response.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        const air = data.ResultData.find((item) => item.Type === 1);
        const water = data.ResultData.find((item) => item.Type === 2);
        const temp = data.ResultData.find((item) => item.Type === 3);

        if (air && Array.isArray(air.Steps)) {
          setAirPressureSelectOptions(
            air.Steps.map((value) => ({
              value: value.toString(),
              label: `${value} ${air.Unit || "bar"}`,
            }))
          );
        } else {
          console.warn("No valid Steps array for Air Pressure:", air);
          setAirPressureSelectOptions([]);
        }

        if (water && Array.isArray(water.Steps)) {
          setPressureOptions(
            water.Steps.map((value) => ({
              value: value.toString(),
              label: `${value} ${water.Unit || "bar"}`,
            }))
          );
        } else {
          console.warn("No valid Steps array for RO Water Pressure:", water);
          setPressureOptions([]);
        }

        if (temp && Array.isArray(temp.Steps)) {
          setTemperatureOptions(
            temp.Steps.map((value) => ({
              value: value.toString(),
              label: `${value} ${temp.Unit || "°C"}`,
            }))
          );
        } else {
          console.warn("No valid Steps array for Temperature:", temp);
          setTemperatureOptions([]);
        }
      } else {
        console.warn("Invalid pressure options data:", data);
        setPressureOptions([]);
        setAirPressureSelectOptions([]);
        setTemperatureOptions([]);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load pressure and temperature options.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      console.error("Error fetching pressure options:", err);
      setPressureOptions([]);
      setAirPressureSelectOptions([]);
      setTemperatureOptions([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load pressure and temperature options.",
        confirmButtonColor: "#d33",
      });
    }
  };

  useEffect(() => {
    fetchData();
    if (selectedBlock) {
      fetchPressureOptions();
    }
  }, [selectedBlock]);

  useEffect(() => {
    if (status === "Wash In") {
      fetch(`${getApiBaseUrl()}${GETMASTERSREASONS}?Type=2`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.Status && Array.isArray(data.ResultData)) {
            setWashingReasons(data.ResultData);
            console.log("Washing reasons fetched:", data.ResultData);
          } else {
            console.warn("Invalid washing reasons data:", data);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Invalid washing reasons data received from server.",
              confirmButtonColor: "#d33",
            });
          }
        })
        .catch((err) => {
          console.error("Failed to load washing reasons:", err);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load washing reasons.",
            confirmButtonColor: "#d33",
          });
        });
    }
  }, [status]);

  const checkStageSequence = async (barcode, orderNum, performedBy) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/CheckOrderedStage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          BarcodeNum: barcode,
          OrderNum: orderNum,
          PerformedBy: performedBy,
        }),
      });
      const result = await response.json();
      const isValid = result.ResultData?.[0]?.Status === "true";
      const currentStage =
        stageNameMapping[selectedAsset?.Status || 0] || "Unknown";
      const newStage = stageNameMapping[orderNum] || "Unknown";
      const message = isValid
        ? ""
        : `Attempted transition from ${currentStage} to ${newStage} for Filter: ${
            selectedAsset?.Lable || "Unknown"
          }`;

      return { isValid, message, data: result };
    } catch (error) {
      console.error("Error checking stage sequence:", error);
      return {
        isValid: false,
        message: `Failed to validate stage sequence for Filter: ${
          selectedAsset?.Lable || "Unknown"
        }`,
        data: null,
      };
    }
  };

  const checkDryerDuration = async (barcode, orderNum, performedBy) => {
    if (status !== "Dry Out") {
      return { isValid: true, message: "" };
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/CheckOrderedStage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          BarcodeNum: barcode,
          OrderNum: 4, // Dry In stage
          PerformedBy: performedBy,
        }),
      });
      const data = await response.json();

      if (!data?.ResultData?.length) {
        return {
          isValid: false,
          message: `No valid Dry In stage found for Filter: ${
            selectedAsset?.Lable || "Unknown"
          }`,
        };
      }

      const record = data.ResultData[0];

      // Skip validation if CreatedTime is missing
      if (!record.CreatedTime) {
        return { isValid: true, message: "" };
      }

      // Parse CreatedTime
      const createdTimeStr = record.CreatedTime.replace("T", " ").replace(
        "Z",
        ""
      );
      const createdTime = new Date(createdTimeStr);
      if (isNaN(createdTime.getTime())) {
        console.warn("Invalid CreatedTime format:", createdTimeStr);
        return { isValid: true, message: "" }; // Skip validation instead of error
      }

      // Skip validation if duration is missing or 0
      const expectedDuration = parseInt(record.duration);
      if (
        !record.duration ||
        isNaN(expectedDuration) ||
        expectedDuration <= 0
      ) {
        return { isValid: true, message: "" };
      }

      // Perform duration validation
      const currentTime = new Date();
      const actualDuration = Math.floor(
        (currentTime - createdTime) / (1000 * 60)
      );

      if (actualDuration < 0) {
        return {
          isValid: false,
          message: `Invalid duration (negative) for Filter: ${
            selectedAsset?.Lable || "Unknown"
          }`,
        };
      }

      if (actualDuration < expectedDuration) {
        return {
          isValid: false,
          message: `Dryer duration is ${actualDuration} minutes, less than the required ${expectedDuration} minutes for Filter: ${
            selectedAsset?.Lable || "Unknown"
          }`,
        };
      }

      return { isValid: true, message: "" };
    } catch (error) {
      console.error("Error checking dryer duration:", error);
      return {
        isValid: false,
        message: `Failed to validate dryer duration for Filter: ${
          selectedAsset?.Lable || "Unknown"
        }`,
      };
    }
  };

  const checkPressureValues = () => {
    if (status === "Wash In" && formData.RoWaterPressure) {
      const pressure = parseFloat(formData.RoWaterPressure);
      const opMin = roPressureRange.operatingMin ?? roPressureRange.min;
      const opMax = roPressureRange.operatingMax ?? roPressureRange.max;
      if (pressure < opMin || pressure > opMax) {
        return {
          isValid: false,
          message: `RO Water Pressure ${pressure} ${
            roPressureRange.unit
          } is outside allowed range (${opMin} - ${opMax} ${
            roPressureRange.unit
          }) for Filter: ${selectedAsset?.Lable || "Unknown"}`,
        };
      }
    }
    if (status === "Dry In") {
      if (formData.AirPressure) {
        const pressure = parseFloat(formData.AirPressure);
        const opMin = airPressureRange.operatingMin ?? airPressureRange.min;
        const opMax = airPressureRange.operatingMax ?? airPressureRange.max;
        if (pressure < opMin || pressure > opMax) {
          return {
            isValid: false,
            message: `Air Pressure ${pressure} ${
              airPressureRange.unit
            } is outside allowed range (${opMin} - ${opMax} ${
              airPressureRange.unit
            }) for Filter: ${selectedAsset?.Lable || "Unknown"}`,
          };
        }
      }
      if (formData.Temperature) {
        const temp = parseFloat(formData.Temperature);
        const opMin = temperatureRange.operatingMin ?? temperatureRange.min;
        const opMax = temperatureRange.operatingMax ?? temperatureRange.max;
        if (temp < opMin || temp > opMax) {
          return {
            isValid: false,
            message: `Temperature ${temp} ${
              temperatureRange.unit
            } is outside allowed range (${opMin} - ${opMax} ${
              temperatureRange.unit
            }) for Filter: ${selectedAsset?.Lable || "Unknown"}`,
          };
        }
      }
    }
    return { isValid: true, message: "" };
  };

  const promptReauthentication = async (deviationMsg) => {
    const result = await Swal.fire({
      title: "Deviation Detected",
      html: `<p>Deviation: ${deviationMsg}</p><p>Please reauthenticate to proceed.</p>`,
      input: "password",
      inputPlaceholder: "Enter your password",
      inputAttributes: {
        autocapitalize: "off",
        autocorrect: "off",
        id: "swal-password-input",
      },
      showCancelButton: true,
      confirmButtonText: "Authenticate",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const input = document.getElementById("swal-password-input");
        if (input) {
          const wrapper = input.parentElement;
          wrapper.style.position = "relative";
          input.style.paddingRight = "2.5rem";
          const toggle = document.createElement("span");
          toggle.innerHTML = `<i class="fa fa-eye"></i>`;
          toggle.style.position = "absolute";
          toggle.style.right = "10px";
          toggle.style.top = "60%";
          toggle.style.transform = "translateY(-50%)";
          toggle.style.cursor = "pointer";
          toggle.style.fontSize = "1.1rem";
          wrapper.appendChild(toggle);

          toggle.addEventListener("click", () => {
            const isPassword = input.type === "password";
            input.type = isPassword ? "text" : "password";
            toggle.innerHTML = isPassword
              ? `<i class="fa fa-eye-slash"></i>`
              : `<i class="fa fa-eye"></i>`;
          });
        }
      },
      preConfirm: async (password) => {
        if (!password.trim()) {
          Swal.showValidationMessage("Password is required!");
          return false;
        }
        try {
          const user = JSON.parse(sessionStorage.getItem("user"));
          const username = user?.UserName || "";
          const response = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
          });

          const resData = await response.json();
          if (resData.status) {
            return true;
          } else {
            Swal.showValidationMessage(
              resData.Message || "Invalid credentials"
            );
            return false;
          }
        } catch (error) {
          Swal.showValidationMessage(
            "Failed to reauthenticate. Please try again."
          );
          return false;
        }
      },
    });

    if (result.isConfirmed && result.value === true) {
      setIsReauthenticated(true);
      Swal.fire({
        icon: "success",
        title: "Reauthentication Successful",
        text: "Authentication successful. Proceeding with submission.",
        confirmButtonColor: "#3085d6",
      });
      return true;
    } else {
      if (result.dismiss === Swal.DismissReason.cancel) {
        console.log("cancelled");
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const performedBy = user?.UserID || 0;
    const performedOn = new Date().toISOString().slice(0, 19).replace("T", " ");
    const description = auditLogDescription[status] || "Status Update";
    const filterId = selectedAsset?.Lable || "Unknown";
    const currentStage =
      stageNameMapping[selectedAsset?.Status || 0] || "Unknown";
    const newStage = stageNameMapping[processOrderMapping[status]] || "Unknown";

    const auditPayloadBase = {
      ActivityType: 1,
      PerformedBy: performedBy,
      PerformedOn: performedOn,
      Notes: `${
        isReauthenticated ? "Manual Override: " : ""
      }${description} for Filter: ${filterId}`,
      Location: "washing",
    };

    if (!status) {
      Swal.fire({
        icon: "warning",
        title: "No Status Selected",
        text: "Please select a status.",
        confirmButtonColor: "#f8bb86",
      });
      return;
    }

    const stage = processOrderMapping[status];
    const missingFieldSet = new Set();

    if (stage === 1) {
      if (!formData.washingReasonId) missingFieldSet.add("Washing Reason");
      if (!formData.RoWaterPressure) missingFieldSet.add("RO Water Pressure");
    }
    if (stage === 3) {
      if (!formData.Duration) missingFieldSet.add("Duration");
      if (!formData.Temperature) missingFieldSet.add("Temperature");
    }
    if (!formData?.Remarks) missingFieldSet.add("Remarks");

    if (missingFieldSet.size > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        text: `Missing: ${Array.from(missingFieldSet).join(", ")}`,
        confirmButtonColor: "#f8bb86",
      });
      return;
    }

    // if (selectedAsset.Status === 0 && status !== "Wash In") {
    //   const errorMessage = `Invalid Status Transition: Filters with 'New' status can only transition to 'Wash In'.`;
    //   await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify({
    //       ...auditPayloadBase,
    //       Notes: `Deviation: Status Transition - ${errorMessage}. Filter: ${filterId}`,
    //       IsSucces: 0,
    //     }),
    //   }).catch((err) =>
    //     console.error("Audit log error (Invalid Status):", err)
    //   );

    //   const notificationPayload = {
    //     Title: "Deviations",
    //     Message: `Deviation detected for Filter ${filterId}: Status Transition - ${errorMessage}`,
    //     RoleID: 4,
    //   };
    //   await fetch(`${getApiBaseUrl()}/api/addNotificationsToRole`, {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify(notificationPayload),
    //   })
    //     .then((response) => response.json())
    //     .then((result) => {
    //       if (result.Status) {
    //         console.log(
    //           "Notification sent successfully for Filter:",
    //           filterId,
    //           notificationPayload
    //         );
    //       } else {
    //         console.warn(
    //           "Notification API failed for Filter:",
    //           filterId,
    //           result
    //         );
    //       }
    //     })
    //     .catch((err) =>
    //       console.error("Notification error (Status Deviation):", err)
    //     );

    //   Swal.fire({
    //     icon: "error",
    //     title: "Invalid Status Transition",
    //     text: errorMessage,
    //     confirmButtonColor: "#d33",
    //   });
    //   return;
    // }

    setIsDeviation(false);
    setIsReauthenticated(false);
    setDeviationMessage("");
    const deviationMessages = [];

    const sequenceResult = await checkStageSequence(
      selectedAsset.Barcode,
      stage,
      performedBy
    );
    if (!sequenceResult.isValid) {
      setIsDeviation(true);
      deviationMessages.push(sequenceResult.message);
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...auditPayloadBase,
          Notes: `Deviation: Cleaning Sequence - ${sequenceResult.message}. `,
          IsSucces: 1,
        }),
      }).catch((err) =>
        console.error("Audit log error (Sequence Deviation):", err)
      );
      const notificationPayload = {
        Title: "Deviations",
        Message: `Deviation detected for Filter ${filterId}: Cleaning Sequence - ${sequenceResult.message}`,
        RoleID: 4,
      };
      await fetch(`${getApiBaseUrl()}/api/addNotificationsToRole`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(notificationPayload),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.Status) {
            console.log(
              "Notification sent successfully for Filter:",
              filterId,
              notificationPayload
            );
          } else {
            console.warn(
              "Notification API failed for Filter:",
              filterId,
              result
            );
          }
        })
        .catch((err) =>
          console.error("Notification error (Sequence Deviation):", err)
        );
    }

    if (status === "Dry Out") {
      const durationResult = await checkDryerDuration(
        selectedAsset.Barcode,
        stage,
        performedBy
      );
      if (!durationResult.isValid) {
        setIsDeviation(true);
        deviationMessages.push(durationResult.message);
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...auditPayloadBase,
            Notes: `Deviation: Dryer Duration - ${durationResult.message}. Filter: ${filterId}`,
            IsSucces: 0,
          }),
        }).catch((err) =>
          console.error("Audit log error (Duration Deviation):", err)
        );
        const notificationPayload = {
          Title: "Deviations",
          Message: `Deviation detected for Filter ${filterId}: Dryer Duration - ${durationResult.message}`,
          RoleID: 4,
        };
        await fetch(`${getApiBaseUrl()}/api/addNotificationsToRole`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(notificationPayload),
        })
          .then((response) => response.json())
          .then((result) => {
            if (result.Status) {
              console.log(
                "Notification sent successfully for Filter:",
                filterId,
                notificationPayload
              );
            } else {
              console.warn(
                "Notification API failed for Filter:",
                filterId,
                result
              );
            }
          })
          .catch((err) =>
            console.error("Notification error (Duration Deviation):", err)
          );
      }
    }

    const pressureResult = checkPressureValues();
    if (!pressureResult.isValid) {
      setIsDeviation(true);
      deviationMessages.push(pressureResult.message);
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...auditPayloadBase,
          Notes: `Deviation: Pressure Validation - ${pressureResult.message}. Filter: ${filterId}`,
          IsSucces: 1,
        }),
      }).catch((err) =>
        console.error("Audit log error (Pressure Deviation):", err)
      );
      const notificationPayload = {
        Title: "Deviations",
        Message: `Deviation detected for Filter ${filterId}: Pressure/Temperature Validation - ${pressureResult.message}`,
        RoleID: 4,
      };
      await fetch(`${getApiBaseUrl()}/api/addNotificationsToRole`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(notificationPayload),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.Status) {
            console.log(
              "Notification sent successfully for Filter:",
              filterId,
              notificationPayload
            );
          } else {
            console.warn(
              "Notification API failed for Filter:",
              filterId,
              result
            );
          }
        })
        .catch((err) =>
          console.error("Notification error (Pressure Deviation):", err)
        );
    }

    if (deviationMessages.length > 0 && !isReauthenticated) {
      setDeviationMessage(deviationMessages.join("; "));
      const reauthSuccess = await promptReauthentication(
        deviationMessages.join("; ")
      );
      if (!reauthSuccess) {
        return;
      }
    }

    const payload = {
      FilterId: selectedAsset.Id || 0,
      StageId: stage,
      Remarks: formData.Remarks.trim() || "",
      Status: stage,
      washingEquipId: stage === 1 ? formData.washingEquipId || 0 : 0,
      AirPressure:
        stage === 1
          ? formData.AirPressure === "N/A"
            ? "N/A"
            : `${formData.AirPressure} ${airPressureRange.unit}`
          : "",
      RoWaterPressure:
        stage === 1
          ? formData.RoWaterPressure === "N/A"
            ? "N/A"
            : `${formData.RoWaterPressure} ${roPressureRange.unit}`
          : "",
      Duration: stage === 3 ? formData.Duration || 0 : 0,
      DryerEquipId: stage === 3 ? formData.DryerEquipId || "" : "",
      DryerTemp:
        stage === 3 ? `${formData.Temperature} ${temperatureRange.unit}` : "",
      performedBy: performedBy,
      Barcode: selectedAsset.Barcode || "",
      washingReasonId: stage === 1 ? formData.washingReasonId || 0 : 0,
      Override: isReauthenticated,
      Platform: "Web",
      blockId: selectedBlock || 0,
    };

    setSubmitLoading(true);
    Swal.fire({
      title: "Submitting...",
      text: "Please wait while we submit your data.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    //region storageout logic
    if (stage === 6) {
      try {
        if (stage === 6) {
          const cleaningRes = await fetch(
            `${getApiBaseUrl()}/api/CheckFilterStatus`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ Barcode: selectedBarcode }),
            }
          );

          const cleaningData = await cleaningRes.json();

          if (!cleaningData?.Status) {
            let filterHtml = "";

            if (
              cleaningData?.NotInStorageFilters &&
              cleaningData.NotInStorageFilters.length > 0
            ) {
              filterHtml = `
            <ul style="text-align:left">
              ${cleaningData.NotInStorageFilters.map(
                (f) => `<li>${f.label} — <b>${f.stage}</b></li>`
              ).join("")}
            </ul>
          `;
            }

            await Swal.fire({
              icon: "error",
              html: `
            <p>${cleaningData?.message || "Some filters not completed."}</p>
            ${filterHtml}
          `,
            });

            setSubmitLoading(false);
            return;
          }

          console.log("Cleaning complete — continue...");
        }

        console.log("Proceed with submit...");

        setSubmitLoading(false);
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Something went wrong!", "error");
      }
    }
    //region storageout logic
    try {
      console.log(isReauthenticated, "isReauthenticated");
      const apiUrl = isReauthenticated
        ? `${getApiBaseUrl()}/api/ManualOverride`
        : `${getApiBaseUrl()}${UPDATEFILTERSTATUS}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify([payload]),
      });

      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (err) {
        console.error("Failed to parse response JSON:", err, responseText);
        throw new Error("Invalid response format from server");
      }

      if (!response.ok) {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...auditPayloadBase,
            Notes: `${
              isReauthenticated ? "Manual Override: " : ""
            }${description} failed for Filter: ${filterId}. HTTP Status: ${
              response.status
            }`,
            IsSucces: 0,
          }),
        }).catch((err) =>
          console.error("Audit log error (HTTP Failure):", err)
        );

        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const isSuccess = result.Status || result.status;
      if (isSuccess) {
        if (isReauthenticated) {
          const overrideMessage = `Deviation: Transition from ${currentStage} to ${newStage} for Filter: ${filterId}.`;
          await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...auditPayloadBase,
              Notes: `Deviation: Override Transition - ${overrideMessage}.`,
              IsSucces: 0,
            }),
          }).catch((err) =>
            console.error("Audit log error (Override Deviation):", err)
          );
          const notificationPayload = {
            Title: "Deviations",
            Message: `Deviation detected for Filter ${filterId}: Override Transition - ${overrideMessage}`,
            RoleID: 4,
          };
          await fetch(`${getApiBaseUrl()}/api/addNotificationsToRole`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(notificationPayload),
          })
            .then((response) => response.json())
            .then((result) => {
              if (result.Status) {
                console.log(
                  "Notification sent successfully for Filter:",
                  filterId,
                  notificationPayload
                );
              } else {
                console.warn(
                  "Notification API failed for Filter:",
                  filterId,
                  result
                );
              }
            })
            .catch((err) =>
              console.error("Notification error (Override Deviation):", err)
            );
          setDeviationMessage(overrideMessage);
        }

        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...auditPayloadBase,
            IsSucces: 1,
          }),
        }).catch((err) => console.error("Audit log error (Success):", err));

        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: `Status updated successfully${
            isReauthenticated ? " (Manual Override)" : ""
          }.`,
          confirmButtonColor: "#3085d6",
        }).then(() => {
          setStatus("");
          setFormData({
            Remarks: "",
            washingEquipId: null,
            washingReasonId: null,
            RoWaterPressure: "",
            DryerEquipId: null,
            Duration: null,
            AirPressure: "",
            Temperature: "",
          });
          setIsDeviation(false);
          setIsReauthenticated(false);
          setDeviationMessage("");
          onClose();
          window.location.reload();
        });
      } else {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...auditPayloadBase,
            Notes: `${
              isReauthenticated ? "Manual Override: " : ""
            }${description} failed: ${
              result.ResultMessage ||
              result.Message ||
              result.message ||
              "Unknown error"
            }. Filter: ${filterId}`,
            IsSucces: 0,
          }),
        }).catch((err) => console.error("Audit log error (API Failure):", err));

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            result.ResultMessage ||
            result.Message ||
            result.message ||
            "Something went wrong",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...auditPayloadBase,
          Notes: `${
            isReauthenticated ? "Manual Override: " : ""
          }${description} error: ${error.message}. Filter: ${filterId}`,
          IsSucces: 0,
        }),
      }).catch((err) => console.error("Audit log error (Catch):", err));

      console.error("Error updating filter status:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong during submission: " + error.message,
        confirmButtonColor: "#d33",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Drawer
      title="Update Filter Status"
      placement="right"
      onClose={() => {
        setStatus("");
        setFormData({
          Remarks: "",
          washingEquipId: null,
          washingReasonId: null,
          RoWaterPressure: "",
          DryerEquipId: null,
          Duration: null,
          AirPressure: "",
          Temperature: "",
        });
        setIsDeviation(false);
        setIsReauthenticated(false);
        setDeviationMessage("");
        onClose();
      }}
      open={visible}
      width={
        window.innerWidth <= 576
          ? "90%"
          : window.innerWidth <= 992
          ? "55%"
          : window.innerWidth <= 1440
          ? "35%"
          : "20%"
      }
      extra={
        <>
          <Button
            type="primary"
            size="small"
            onClick={handleSubmit}
            disabled={submitLoading}
            style={{ marginRight: 8 }}
          >
            Submit
          </Button>
        </>
      }
    >
      {submitLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ flex: "1 1 45%", minWidth: 200 }}>
            <label style={{ display: "block", marginBottom: 8 }}>
              Filter ID
            </label>
            <Input value={selectedAsset?.Lable || ""} readOnly />
          </div>
          <div style={{ flex: "1 1 45%", minWidth: 200 }}>
            <label style={{ display: "block", marginBottom: 8 }}>
              New Status
            </label>
            <Select
              style={{ width: "100%" }}
              value={status}
              onChange={(value) => setStatus(value)}
              placeholder="Select Status"
            >
              <Option value="">Select Status</Option>
              <Option value="Wash In">Wash In</Option>
              <Option value="Wash Out">Wash Out</Option>
              <Option value="Dry In">Dry In</Option>
              <Option value="Dry Out">Dry Out</Option>
              <Option value="Storage-In">Storage-In</Option>
              <Option value="Cleaning Cycle Completed">Storage-Out</Option>
            </Select>
          </div>
          <div style={{ flex: "1 1 100%", marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>Remarks</label>
            <TextArea
              rows={3}
              value={formData.Remarks}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  Remarks: e.target.value.trimStart(),
                }))
              }
            />
          </div>
          {status === "Wash In" && (
            <>
              <div
                className="d-none"
                style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}
              >
                <label style={{ display: "block", marginBottom: 8 }}>
                  Washing Equipment
                </label>
                <Select
                  showSearch
                  placeholder="-- Select Equipment --"
                  optionFilterProp="children"
                  value={formData.washingEquipId || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      washingEquipId: value,
                    }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  {newEquipmentsData.map((equip) => (
                    <Option key={equip.Id} value={equip.Id}>
                      {equip.EquipmentName} ({equip.SerialNumber})
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Washing Reason
                </label>
                <Select
                  showSearch
                  placeholder="-- Select Reason --"
                  optionFilterProp="children"
                  value={formData.washingReasonId || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      washingReasonId: Number(value),
                    }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  {washingReasons.map((reason) => (
                    <Option key={reason.Id} value={reason.Id}>
                      {reason.Reason}
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  RO Water Pressure
                </label>
                <Select
                  showSearch
                  placeholder="RO Water Pressure"
                  optionFilterProp="children"
                  value={formData.RoWaterPressure || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      RoWaterPressure: value,
                    }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  <Option key="N/A" value="N/A">
                    N/A
                  </Option>
                  {pressureOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Air Pressure
                </label>
                <Select
                  showSearch
                  placeholder="Air Pressure"
                  optionFilterProp="children"
                  value={formData.AirPressure || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, AirPressure: value }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  <Option key="N/A" value="N/A">
                    N/A
                  </Option>
                  {airPressureSelectOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </>
          )}
          {status === "Dry In" && (
            <>
              <div
                className="d-none"
                style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}
              >
                <label style={{ display: "block", marginBottom: 8 }}>
                  Dryer Equipment
                </label>
                <Select
                  showSearch
                  placeholder="Select Dryer Equipment"
                  optionFilterProp="children"
                  value={formData.DryerEquipId || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      DryerEquipId: value,
                    }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  {newEquipmentsData.map((equip) => (
                    <Option key={equip.Id} value={equip.Id}>
                      {equip.EquipmentName} ({equip.SerialNumber})
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Duration
                </label>
                <Input
                  type="number"
                  placeholder="Enter duration"
                  value={formData.Duration || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      Duration: parseInt(e.target.value, 10) || null,
                    }))
                  }
                  addonAfter="min"
                  size="middle"
                />
              </div>
              <div style={{ flex: "1 1 45%", minWidth: 200, marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>
                  Temperature
                </label>
                <Select
                  showSearch
                  placeholder="Temperature"
                  optionFilterProp="children"
                  value={formData.Temperature || undefined}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, Temperature: value }))
                  }
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                >
                  {temperatureOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </>
          )}
          {isDeviation && (
            <div style={{ flex: "1 1 100%", color: "red", marginBottom: 16 }}>
              {deviationMessage}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

StatusUpdate.propTypes = {
  selectedAsset: PropTypes.shape({
    Id: PropTypes.number,
    FilterId: PropTypes.string,
    Barcode: PropTypes.string,
    Status: PropTypes.number,
    AHUId: PropTypes.number,
    Location: PropTypes.string,
    LastCleaningDate: PropTypes.string,
    cleaningdays: PropTypes.number,
    Lable: PropTypes.string,
  }).isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StatusUpdate;
